// Telemetry: the datalogs the account's TUNE MFDs sent (each car's latest 50), as TUNE's analytics site lists them:
// searched by date, car or circuit, for one car or every car, and sorted by when or size; a table on a desktop and rows
// on a phone. Each opens in the Analyser, downloads, or goes. With none yet, TUNE's sample is there to try.
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import type { components } from '@tune/shared';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { getCachedDatalogCircuits, type CircuitCacheEntry } from '../../analyzer/site/lib/datalogCircuitCache';
import { matchesDatalogSearch, parseDatalogName } from '../../analyzer/site/lib/datalogName';
import { data, problem } from '../../api';
import { useAuth } from '../../auth';
import { fetchFile, size } from '../../items';
import { forgetDatalog } from '../../lib/datalogs';
import { dayText, clockText, whenText } from '../../lib/words';
import { keys, LATEST_DATALOGS, latestDatalogs, useVehicles } from '../../queries';
import { Page } from '../../shell/Page';
import {
  Banner,
  ButtonLink,
  DataTable,
  EmptyState,
  ErrorBanner,
  IconButton,
  Menu,
  SearchField,
  Select,
  Skeleton,
  useConfirm,
  useNow,
  useToast,
  type Column,
} from '../../ui';
import {
  ChartLineIcon,
  DotsThreeIcon,
  DownloadSimpleIcon,
  FileArrowUpIcon,
  FlagCheckeredIcon,
  TrashIcon,
} from '../../ui/icons';
import s from './Telemetry.module.css';

type Datalog = components['schemas']['Datalog'];
type Vehicle = components['schemas']['Vehicle'];

export interface Row {
  car: Vehicle;
  log: Datalog;
  when: string;
  circuit: CircuitCacheEntry | undefined;
}

/** Whether a row is what's searched for: a date (as its file names it, or as shown), a car, or a circuit. */
export function matches(row: Row, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) {
    return true;
  }
  const { log } = row;
  return (
    matchesDatalogSearch(
      { name: log.fileName, updatedAt: log.receivedAt },
      parseDatalogName(log.fileName),
      new Date(row.when),
      q,
    ) ||
    row.car.nickname.toLowerCase().includes(q) ||
    (row.circuit?.trackName ?? '').toLowerCase().includes(q) ||
    `${dayText(row.when)} ${clockText(row.when)}`.toLowerCase().includes(q)
  );
}

export function Telemetry() {
  const api = useAuth().api('owners');
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const say = useToast();
  const now = useNow(60_000);
  const vehicles = useVehicles();
  const [car, setCar] = useState('');
  const [search, setSearch] = useState('');
  const cars = useMemo(() => vehicles.data?.vehicles ?? [], [vehicles.data]);
  const lists = useQueries({ queries: cars.map((v) => latestDatalogs(api, v.id)) });
  const download = useMutation({
    mutationFn: (r: Row) =>
      data(
        api.GET('/vehicles/{vehicleId}/datalogs/{datalogId}/download', {
          params: { path: { vehicleId: r.car.id, datalogId: r.log.id } },
        }),
      ),
    onSuccess: (link) => fetchFile(link.url),
  });
  const remove = useMutation({
    mutationFn: (r: Row) =>
      data(
        api.DELETE('/vehicles/{vehicleId}/datalogs/{datalogId}', {
          params: { path: { vehicleId: r.car.id, datalogId: r.log.id } },
        }),
      ),
    onSuccess: async (_answer, r) => {
      await forgetDatalog(r.log.id);
      await queryClient.invalidateQueries({ queryKey: keys.datalogs(r.car.id) });
      say('The datalog is deleted.');
    },
  });
  const ask = async (r: Row) => {
    const yes = await confirm({
      title: 'Delete this datalog?',
      message: `${r.car.nickname}'s datalog of ${whenText(r.when)} goes from TUNE's cloud for good. A copy on the MFD stays there.`,
      confirm: 'Delete',
    });
    if (yes) {
      remove.mutate(r);
    }
  };

  const loading = vehicles.isPending || lists.some((q) => q.isPending);
  const failed = vehicles.error ?? lists.find((q) => q.isError)?.error;
  const circuits = getCachedDatalogCircuits();
  const all: Row[] = cars.flatMap((v, i) =>
    (lists[i]?.data?.datalogs ?? []).map((log) => ({
      car: v,
      log,
      when: log.startedAt ?? log.receivedAt,
      circuit: circuits[log.id],
    })),
  );
  const shown = all.filter((r) => (!car || r.car.id === car) && matches(r, search));
  const more = cars.filter((_, i) => lists[i]?.data?.next);

  const columns: Array<Column<Row>> = [
    {
      key: 'recorded',
      header: 'Recorded',
      phone: 'title',
      compare: (a, b) => a.when.localeCompare(b.when),
      cell: (r) => (
        <span className={s.when}>
          <span>
            <span className="visually-hidden">Analyse the datalog of </span>
            {whenText(r.when, now)}
          </span>
          {r.circuit?.isCircuit && (
            <span className={s.circuit} title={`Laps of ${r.circuit.trackName ?? 'a circuit'}`}>
              <FlagCheckeredIcon size={14} weight="bold" aria-hidden="true" />
              <span className="visually-hidden">, laps of {r.circuit.trackName ?? 'a circuit'}</span>
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'car',
      header: 'Car',
      phone: 'detail',
      cell: (r) => (
        <>
          {r.car.nickname}
          {r.circuit?.trackName && <span className={s.dim}> · {r.circuit.trackName}</span>}
        </>
      ),
    },
    {
      key: 'size',
      header: 'Size',
      phone: 'detail',
      align: 'end',
      compare: (a, b) => a.log.size - b.log.size,
      cell: (r) => size(r.log.size),
    },
    {
      key: 'more',
      header: 'More',
      phone: 'end',
      align: 'end',
      cell: (r) => (
        <Menu
          label={`The datalog of ${whenText(r.when, now)}`}
          entries={[
            {
              label: 'Download',
              icon: <DownloadSimpleIcon size={18} weight="bold" />,
              onSelect: () => download.mutate(r),
            },
            {
              label: 'Delete',
              icon: <TrashIcon size={18} weight="bold" />,
              destructive: true,
              onSelect: () => void ask(r),
            },
          ]}
          trigger={(props) => (
            <IconButton
              {...props}
              label={`More for ${r.car.nickname}'s datalog of ${whenText(r.when, now)}`}
              icon={<DotsThreeIcon size={20} weight="bold" />}
            />
          )}
        />
      ),
    },
  ];

  return (
    <Page
      title="Telemetry"
      root
      subtitle="The datalogs your TUNE MFDs sent, to analyse here: charts, the map, laps and more."
      headActions={
        <ButtonLink to="/telemetry/file" size="small" variant="gray" icon={<FileArrowUpIcon size={16} weight="bold" />}>
          Open a datalog
        </ButtonLink>
      }
    >
      {failed && <ErrorBanner retry={() => void vehicles.refetch()}>{problem(failed)}</ErrorBanner>}
      {(download.error ?? remove.error) && <Banner tone="error">{problem(download.error ?? remove.error)}</Banner>}
      {loading && <Skeleton blocks={1} lines={5} label="Loading your datalogs" />}
      {!loading && !failed && all.length === 0 && (
        <EmptyState
          icon={<ChartLineIcon size={56} />}
          title="No datalogs yet"
          action={<ButtonLink to="/telemetry/sample">Try a sample session</ButtonLink>}
        >
          Record on the MFD, on its Datalogs page. It sends each datalog once it's on Wi-Fi and the car is stopped.
          Until then, try TUNE's sample: its charts, map and laps.
        </EmptyState>
      )}
      {all.length > 0 && (
        <>
          <div className={s.filters}>
            <SearchField
              label="Search datalogs"
              placeholder="A date, a car or a circuit"
              value={search}
              onValueChange={setSearch}
            />
            {cars.length > 1 && (
              <Select label="Car" hideLabel value={car} onChange={(e) => setCar(e.target.value)}>
                <option value="">Every car</option>
                {cars.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.nickname}
                  </option>
                ))}
              </Select>
            )}
          </div>
          {(search.trim() || car) && (
            <p className={s.count} role="status">
              Showing {shown.length} of {all.length} datalog{all.length === 1 ? '' : 's'}
            </p>
          )}
          {shown.length === 0 ? (
            <EmptyState title="No datalogs match">Try another date, car or circuit.</EmptyState>
          ) : (
            <DataTable
              label="Your datalogs"
              columns={columns}
              rows={shown}
              rowKey={(r) => r.log.id}
              rowLink={(r) => `/telemetry/${r.car.id}/${r.log.id}`}
              sort={{ key: 'recorded', descending: true }}
            />
          )}
          {more.length > 0 && (
            <p className={s.note}>
              These are each car's latest {LATEST_DATALOGS}. Older ones are on{' '}
              {more.map((v, i) => (
                <span key={v.id}>
                  {i > 0 && (i === more.length - 1 ? ' and ' : ', ')}
                  <Link to={`/garage/${v.id}/recordings?show=datalogs`}>{v.nickname}'s recordings</Link>
                </span>
              ))}
              .
            </p>
          )}
        </>
      )}
    </Page>
  );
}
